import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { CtaLink, Headline, Kicker, Reveal, Section } from '../ui';
import { PLANS, PLAN_ORDER, perMonthLabel, planRank, type BillingInterval, type PlanId } from '@/lib/plans';
import { usePlan, startCheckout } from '@/hooks/usePlan';
import { useAuth } from '@/stores/auth';
import { toast } from '@/components/ui/use-toast';
import { cn, errorMessage } from '@/lib/utils';

const FAQ: Array<[string, string]> = [
  ['Why is there a Shop plan?', 'Because we take 0% of your sales. Marketplaces charge 5 to 15 percent; we charge a flat month instead, so the more you sell the better the deal gets.'],
  ['Do my customers need an account?', 'No. They open your card, add to an order, and pay you through Venmo, Cash App, PayPal, Zelle, Square or by card. No app, no signup.'],
  ['Who pays Stripe’s fees on card orders?', 'You do, in your own Stripe account, exactly as if you’d sold through Stripe directly. Haphe adds nothing on top.'],
  ['Can I change or cancel any time?', 'Yes. Plans are monthly or yearly, managed through the Stripe customer portal from Settings. Cancel and you drop to Free at the end of the period; your cards stay.'],
  ['What happens to my extra cards if I go back to Free?', 'They stay. Free includes one card going forward; existing cards aren’t deleted.'],
  ['Is there a free trial?', 'Free is the trial. It has the whole card design, exchange, and the leads inbox. Upgrade when you want a second card, the shop, or the follow-up tools.'],
];

export function PricingPage() {
  const [interval, setInterval_] = useState<BillingInterval>('monthly');
  const [busy, setBusy] = useState<PlanId | null>(null);
  const [params] = useSearchParams();
  const highlight = params.get('plan') as PlanId | null;
  const user = useAuth((s) => s.user);
  const planInfo = usePlan();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.get('billing') === 'cancelled') toast({ title: 'No charge made', description: 'You can pick a plan whenever you like.' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function choose(id: PlanId) {
    if (id === 'free') {
      navigate(user ? '/app' : '/login');
      return;
    }
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/pricing?plan=${id}`)}`);
      return;
    }
    setBusy(id);
    try {
      window.location.href = await startCheckout(id, interval);
    } catch (e) {
      toast({ title: 'Could not start checkout', description: errorMessage(e), variant: 'destructive' });
      setBusy(null);
    }
  }

  return (
    <>
      <Section className="play-subhero play-subhero--pricing pb-10 pt-16">
        <Reveal className="text-center">
          <Kicker>Honest plans · no mystery math</Kicker>
          <h1 className="play-subhero__title mt-4">
            Free to start. <em>Fair when it earns.</em>
          </h1>
          <p className="mx-auto mt-5 max-w-lg text-base text-ink-muted">
            The card is always free. Pay for more cards, the follow-up tools, or a shop that keeps
            every dollar you make.
          </p>
          <div className="play-billing-toggle mt-8 inline-flex items-center rounded-full border border-bone-deep bg-surface p-1">
            {(['monthly', 'yearly'] as const).map((i) => (
              <button
                key={i}
                onClick={() => setInterval_(i)}
                aria-pressed={interval === i}
                className={cn('h-9 rounded-full px-4 text-sm font-medium transition-colors', interval === i ? 'bg-ink text-bone' : 'text-ink-muted')}
              >
                {i === 'monthly' ? 'Monthly' : 'Yearly'}
                {i === 'yearly' && <span className="ml-1.5 rounded-full bg-accent/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-accent">2 months free</span>}
              </button>
            ))}
          </div>
        </Reveal>
      </Section>

      <Section className="play-pricing-grid pb-24">
        <div className="grid gap-4 lg:grid-cols-4">
          {PLAN_ORDER.map((id, i) => {
            const p = PLANS[id];
            const current = planInfo.plan === id && !planInfo.isLoading && !!user;
            const isHighlight = highlight === id || (!highlight && p.featured);
            const downgrade = !!user && planRank(id) < planRank(planInfo.plan);
            return (
              <Reveal key={id} delay={i * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className={cn(
                    'play-plan relative flex h-full flex-col rounded-3xl border p-6',
                    isHighlight ? 'border-ink bg-ink text-bone shadow-[0_40px_80px_-40px_rgba(10,9,8,0.6)]' : 'border-bone-deep bg-surface',
                  )}
                >
                  {p.featured && (
                    <span className="absolute -top-3 left-6 rounded-full bg-[oklch(0.8_0.13_78)] px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-ink">
                      most picked
                    </span>
                  )}
                  <p className={cn('mk-kicker', isHighlight ? 'mk-gold' : 'text-ink-muted')}>{p.name}</p>
                  <p className="mt-3 text-4xl font-medium tracking-tightest">
                    {perMonthLabel(p, interval)}
                    <span className={cn('text-base', isHighlight ? 'text-bone/60' : 'text-ink-muted')}> / mo</span>
                  </p>
                  <p className={cn('mt-1 text-xs', isHighlight ? 'text-bone/50' : 'text-ink-muted')}>
                    {p.monthlyCents === 0 ? 'forever' : interval === 'yearly' ? `$${p.yearlyCents / 100} billed yearly` : 'billed monthly'}
                  </p>
                  <p className={cn('mt-3 text-sm font-medium', isHighlight ? 'text-bone' : 'text-ink')}>{p.headline}</p>
                  <ul className={cn('mt-4 flex-1 space-y-2 text-sm', isHighlight ? 'text-bone/80' : 'text-ink-soft')}>
                    {p.includes.map((l) => (
                      <li key={l} className="flex items-start gap-2">
                        <Check className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', isHighlight ? 'mk-gold' : 'text-accent')} />
                        {l}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => choose(id)}
                    disabled={busy !== null || current || (!p.purchasable && id !== 'free')}
                    className={cn(
                      'mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-full text-sm font-medium transition-colors disabled:opacity-60',
                      isHighlight ? 'bg-[oklch(0.8_0.13_78)] text-ink hover:brightness-105' : 'bg-ink text-bone hover:bg-ink-soft',
                    )}
                  >
                    {busy === id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : current ? (
                      'Your plan'
                    ) : id === 'teams' ? (
                      'Coming soon'
                    ) : id === 'free' ? (
                      user ? 'Included' : 'Get started'
                    ) : downgrade ? (
                      'Change in Settings'
                    ) : (
                      `Choose ${p.name}`
                    )}
                  </button>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-8 text-center text-sm text-ink-muted">
          Prices in USD. Card payments in the Shop go through your own Stripe account; Stripe's
          processing fee applies there, nothing from Haphe.{' '}
          {user && (
            <>
              Manage an existing subscription from <Link to="/app/settings" className="underline underline-offset-2">Settings</Link>.
            </>
          )}
        </Reveal>
      </Section>

      <Section dark className="play-page-dark play-faq py-24">
        <Reveal className="max-w-2xl">
          <Kicker gold>Questions</Kicker>
          <Headline className="mt-4 text-bone">The short answers.</Headline>
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {FAQ.map(([q, a], i) => (
            <Reveal key={q} delay={i * 0.04}>
              <div className="h-full rounded-2xl border border-bone/10 bg-white/[0.03] p-5">
                <p className="text-sm font-medium tracking-tight text-bone">{q}</p>
                <p className="mt-2 text-sm leading-relaxed text-bone/65">{a}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 text-center">
          <CtaLink to="/signup" variant="gold">Get your card, free</CtaLink>
        </Reveal>
      </Section>
    </>
  );
}
